import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Chip, IconButton, Button, Typography, Autocomplete, TextField, InputAdornment } from '@mui/material';
import * as Icons from '@mui/icons-material';
import { useTheme } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import RecipeFilters from './RecipeFilters.js';

function RecipeSearch({
  searchInput,
  setSearchInput,
  searchTerms,
  setSearchTerms,
  debouncedSearchInput,
  statusFilters,
  setStatusFilters,
  originFilters,
  setOriginFilters,
  sortOrder,
  setSortOrder,
  navigate,
  originFilter,
  uniqueOrigins,
  recipes,
  inventory,
  onFilterHeightChange,
  onSetChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [activeSetId, setActiveSetId] = useState('');
  const [activeSet, setActiveSet] = useState(null);

  const searchContainerRef = useRef(null);
  const inputRef = useRef(null);

  // Load ingredient sets
  useEffect(() => {
    // The RecipeFilters component will handle loading the sets and updating the active set
    // We're just initializing the state here
  }, []);

  // Memoize suggestions to avoid recalculating on every render
  const suggestions = useMemo(() => {
    // Build suggestions from inventory and recipes.
    let result = [];

    // Add fallback 'search for: <query>' option at the top if search input is not empty
    if (searchInput.trim() !== '') {
      result.push({ 
        type: 'search', 
        id: 'search', 
        name: `Search for: ${searchInput}`,
        group: '' // Empty group for search option
      });
    }

    // Cocktails (recipes)
    const cocktailSuggestions = recipes.map(recipe => ({
      type: 'recipe',
      id: recipe.id,
      name: `${recipe.name} (${recipe.origin})`,
      rawName: recipe.name,
      origin: recipe.origin,
      group: 'Cocktails' // Add group property instead of separate entry
    })).filter(recipe => 
      recipe.rawName.toLowerCase().includes(searchInput.toLowerCase()) &&
      !searchTerms.includes(recipe.rawName)
    );

    // Add cocktail suggestions (limited to 5)
    if (cocktailSuggestions.length > 0) {
      result = result.concat(cocktailSuggestions.slice(0, 5));
    }

    // Ingredients
    const ingredientSuggestions = inventory.map(ing => ({
      type: 'ingredient',
      id: ing.id,
      name: ing.name,
      group: 'Ingredients' // Add group property instead of separate entry
    })).filter(item => 
      item.name.toLowerCase().includes(searchInput.toLowerCase()) &&
      !searchTerms.includes(item.name)
    );

    // Add ingredient suggestions (limited to 5)
    if (ingredientSuggestions.length > 0) {
      result = result.concat(ingredientSuggestions.slice(0, 5));
    }

    // Add 'no matches' option if needed
    if (result.length === 0 && searchInput.trim() !== '') {
      result = [{ 
        type: 'no-matches', 
        id: 'no-matches', 
        name: 'No matches found', 
        disabled: true,
        group: ''
      }];
    }
    
    return result;
  }, [inventory, recipes, searchInput, searchTerms]);

  useEffect(() => {
    if (searchInput === '') {
      setIsClearing(false);
    }
  }, [searchInput]);

  // Debounce the height measurement to reduce unnecessary operations
  useEffect(() => {
    let timeoutId;
    const updateHeight = () => {
      if (searchContainerRef.current && onFilterHeightChange) {
        const totalHeight = searchContainerRef.current.getBoundingClientRect().height;
        onFilterHeightChange(totalHeight);
      }
    };
    
    clearTimeout(timeoutId);
    timeoutId = setTimeout(updateHeight, 100);
    
    return () => clearTimeout(timeoutId);
  }, [searchTerms, statusFilters, originFilters, sortOrder, onFilterHeightChange, activeSetId]);

  // Handle selection from Autocomplete (for explicit selections like clicking/tapping).
  const handleOptionSelect = useCallback((event, value, reason) => {
    if (reason === 'selectOption') {
      if (typeof value === 'object' && value.type !== 'no-matches') {
        if (value.type === 'recipe') {
          // For direct cocktail selection, bypass filters and go directly to detail view
          navigate(`/recipe/${value.id}`);
        } else if (value.type === 'ingredient') {
          // For ingredient selection, add as a chip to filter current list
          const chipName = value.name;
          if (!searchTerms.includes(chipName)) {
            setSearchTerms([...searchTerms, chipName]);
          }
        } else if (value.type === 'search') {
          // For full-text search, respect current filters and add search input as a term
          if (searchInput.trim() && !searchTerms.includes(searchInput)) {
            setSearchTerms([...searchTerms, searchInput]);
          }
        }
      }
      setIsClearing(true);
      setSearchInput('');
      setOpen(false);
      if (inputRef.current) {
        if (isMobile) {
          inputRef.current.blur();
        } else {
          setTimeout(() => inputRef.current.focus(), 0);
        }
      }
    }
  }, [searchTerms, setSearchTerms, searchInput, navigate, isMobile]);

  const handleInputChange = useCallback((event, newValue) => {
    if (!isClearing) {
      setSearchInput(newValue || '');
      setOpen(!!newValue);
    }
  }, [isClearing, setSearchInput]);

  const removeSearchTerm = useCallback((termToRemove) => {
    setSearchTerms(searchTerms.filter(term => term !== termToRemove));
  }, [searchTerms, setSearchTerms]);

  const handleClearAll = useCallback(() => {
    setSearchInput('');
    setSearchTerms([]);
    setStatusFilters([]);
    setOriginFilters([]);
    setSortOrder('asc');
    setOpen(false);
    
    // Clear ingredient set too
    localStorage.removeItem('activeIngredientSet');
    setActiveSetId('');
    setActiveSet(null);
    if (onSetChange) {
      onSetChange(null);
    }
    
    // Also clear localStorage
    localStorage.removeItem('recipeSearchTerms');
    localStorage.removeItem('recipeStatusFilters');
    localStorage.removeItem('recipeOriginFilters');
    localStorage.setItem('recipeSortOrder', 'asc');
    
    if (inputRef.current) {
      if (isMobile) {
        inputRef.current.blur();
      } else {
        setTimeout(() => inputRef.current.focus(), 0);
      }
    }
  }, [setSearchInput, setSearchTerms, setStatusFilters, setOriginFilters, setSortOrder, isMobile, onSetChange]);

  // Custom handler for keydown events
  const handleKeyDown = useCallback((event) => {
    if (isMobile && event.key === 'Enter') {
      event.preventDefault(); // Prevent default behavior (selection or submission)
      event.stopPropagation(); // Stop event from reaching Autocomplete
      // Do nothing else: keep keyboard up, input intact, suggestions open
    }
    // On desktop, let Autocomplete handle Enter normally for selection
  }, [isMobile]);

  const hasChips =
    statusFilters.length > 0 || originFilters.length > 0 || sortOrder !== 'asc' || searchTerms.length > 0 || activeSet;

  return (
    <Box ref={searchContainerRef}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            onClick={() => navigate('/')}
            size={isMobile ? 'medium' : 'small'}
            sx={{ textTransform: 'none' }}
          >
            <Icons.ArrowBack sx={{ mr: 0.5 }} />
            Collections
          </Button>
          <Typography component="span" sx={{ mx: 1, color: theme.palette.text.secondary }}>
            |
          </Typography>
          <Typography component="span" sx={{ color: theme.palette.text.secondary }}>
            {originFilter || 'All Recipes'}
          </Typography>
        </Box>
      </Box>

      {/* Search Bar with inline Filter Button */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        mb: 2, 
        gap: 1,
        flexDirection: isMobile ? 'column' : 'row', 
        width: '100%',
      }}>
        <Autocomplete
          freeSolo
          clearOnBlur={false}
          blurOnSelect={false}
          value={null}
          options={suggestions}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
          inputValue={searchInput}
          onInputChange={handleInputChange}
          onChange={handleOptionSelect}
          filterOptions={(options) => options}
          open={open}
          onClose={() => {}}
          autoHighlight
          autoSelect={false}
          groupBy={(option) => option.group || ''}
          renderGroup={(params) => (
            <li key={params.key}>
              {params.group && params.group !== '' ? (
                <Typography variant="overline" sx={{ fontWeight: 'bold', paddingLeft: 1, fontSize: '0.7rem' }}>
                  {params.group}
                </Typography>
              ) : null}
              <ul style={{ padding: 0 }}>{params.children}</ul>
            </li>
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={option.disabled ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {option.type === 'ingredient' ? (
                  <Icons.Spa fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                ) : option.type === 'recipe' ? (
                  <Icons.LocalBar fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                ) : option.type === 'search' ? (
                  <Icons.Search fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                ) : null}
                <Typography
                  variant="body2"
                  color={option.disabled ? 'text.secondary' : 'text.primary'}
                  sx={selected ? { fontWeight: 'bold', backgroundColor: 'action.selected' } : {}}
                >
                  {option.name}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={inputRef}
              placeholder="Search recipes or ingredients..."
              variant="outlined"
              fullWidth
              onKeyDown={handleKeyDown}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {searchInput && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchInput('');
                          setOpen(false);
                        }}
                      >
                        <Icons.Clear fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => {
                      if (searchInput.trim() && !searchTerms.includes(searchInput)) {
                        setSearchTerms([...searchTerms, searchInput]);
                        setSearchInput('');
                        setOpen(false);
                      }
                    }}>
                      <Icons.Search fontSize="small" />
                    </IconButton>
                  </Box>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 4,
                  backgroundColor: theme.palette.background.paper,
                }
              }}
            />
          )}
          sx={{ width: '100%' }}
        />
        
        {/* Filter Button - Inline with search */}
        <RecipeFilters
          statusFilters={statusFilters}
          setStatusFilters={setStatusFilters}
          originFilters={originFilters}
          setOriginFilters={setOriginFilters}
          uniqueOrigins={uniqueOrigins}
          originFilter={originFilter}
          activeSetId={activeSetId}
          setActiveSetId={setActiveSetId}
          activeSet={activeSet}
          setActiveSet={setActiveSet}
          onSetChange={onSetChange}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
        />
      </Box>

      {/* Active Set Indicator for Mobile - Integrated with filter chips */}
      {hasChips && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: isMobile ? 0.5 : 0.75,
            mt: 0,
            mb: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: isMobile ? 0.5 : 0.75,
              maxWidth: '100%',
            }}
          >
            {searchTerms.map(term => (
              <Chip
                key={term}
                label={term}
                onDelete={() => removeSearchTerm(term)}
                size="small"
                color="secondary"
                variant="outlined"
              />
            ))}
            {statusFilters.map(filter => (
              <Chip
                key={filter}
                label={
                  filter === 'favorites'
                    ? 'Favorites'
                    : filter === 'can-make'
                    ? 'Can Make'
                    : `Missing ${filter.split('-')[1]} Ingredient${filter.split('-')[1] > 1 ? 's' : ''}`
                }
                onDelete={() => setStatusFilters(statusFilters.filter(f => f !== filter))}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
            {originFilters
              .filter(origin => origin !== originFilter)
              .map(origin => (
                <Chip
                  key={origin}
                  label={`Origin: ${origin}`}
                  onDelete={() => setOriginFilters(originFilters.filter(o => o !== origin))}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            {sortOrder !== 'asc' && (
              <Chip
                label={`Sorted ${sortOrder === 'desc' ? 'Z-A' : 'A-Z'}`}
                onDelete={() => setSortOrder('asc')}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {activeSet && (
              <Chip
                key="active-set"
                label={`Set: ${activeSet.name}`}
                onDelete={() => {
                  localStorage.removeItem('activeIngredientSet');
                  setActiveSetId('');
                  setActiveSet(null);
                  if (onSetChange) {
                    onSetChange(null);
                  }
                }}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <Box sx={{ marginLeft: 'auto' }}>
            {isMobile ? (
              <Button
                onClick={handleClearAll}
                sx={{
                  backgroundColor: theme.palette.action.hover,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: theme.palette.action.selected },
                  minWidth: 'auto',
                  padding: '2px 8px',
                  height: '24px',
                  fontSize: '0.75rem',
                }}
              >
                Clear
              </Button>
            ) : (
              <Button
                startIcon={<Icons.ClearAll />}
                onClick={handleClearAll}
                sx={{
                  backgroundColor: theme.palette.action.hover,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: theme.palette.action.selected },
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default RecipeSearch;